
import { defineComponent, inject } from 'vue'
import Checkbox from '@/components/shared/Checkbox.vue'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import Dropdown from '@/components/shared/Dropdown.vue'
import TextArea from '@/components/shared/TextArea.vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { ServiceService } from '@/services/service.service'
import { ServiceDetails } from '@/@types/responses/service/ServiceDetails'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { BuildStepService } from '@/services/buildStep.service'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { ServiceFieldService } from '@/services/serviceField.service'
import { ServiceField } from '@/@types/responses/service/ServiceField'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import ServiceFieldModal from '@/components/modals/ServiceFieldModal.vue'
import { CreateServiceMessage } from '@/@types/messages/CreateServiceMessage'
import { UpdateServiceMessage } from '@/@types/messages/UpdateServiceMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Service',
  components: {
    AuditInformation,
    Checkbox,
    CurrencyInput,
    Dropdown,
    LoadingPulse,
    LockingButton,
    ServiceFieldModal,
    TextArea,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showServiceFieldModal) {
      this.showServiceFieldModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const buildStepService = inject(PROVIDER_CONSTANTS.buildStepService) as BuildStepService
    const serviceFieldService = inject(PROVIDER_CONSTANTS.serviceFieldService) as ServiceFieldService
    const serviceService = inject(PROVIDER_CONSTANTS.serviceService) as ServiceService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      buildStepService,
      serviceFieldService,
      serviceService,
      toast
    }
  },
  data() {
    let service: ServiceDetails|undefined
    let serviceFieldToEdit: ServiceField|undefined
    let unchangedService: ServiceDetails|undefined

    return {
      loading: true,
      buildStepDropdownOptions: [] as DropdownOptionResponse<number>[],
      service,
      saving: false,
      serviceFieldToEdit,
      showServiceFieldModal: false,
      unchangedService,
    }
  },
  computed: {
    buildStepText(): string {
      if (!this.service) return ''

      const index = this.buildStepDropdownOptions.findIndex(t => t.id === this.service?.buildStepId)
      if (index === -1) return ''

      return this.buildStepDropdownOptions[index].name
    },
    isDirty(): boolean {
      const a = this.service
      const b = this.unchangedService

      return (a?.name !== b?.name ||
             a?.description !== b?.description ||
             a?.price !== b?.price ||
             a?.sku !== b?.sku ||
             a?.buildStepId !== b?.buildStepId ||
             a?.allowDiscounts !== b?.allowDiscounts) && (a?.buildStepId !== undefined && a?.name.trim() !== '' && a?.sku.trim() !== '' && a?.price >= 0)
    },
    titleText(): string {
      return !this.service || this.service.id === 0 ? 'Create Service' : 'Edit Service'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    this.getBuildSteps().then(() => {
      if (this.id > 0) {
        this.getService()
      } else {
        this.service = new ServiceDetails(0)
        this.unchangedService = new ServiceDetails(0)
        this.loading = false
      }
    })
  },
  methods: {
    getBuildSteps(): Promise<void> {
      return this.buildStepService.getBuildStepDropdownOptions().then(dropdownOptionsResponse => {
        this.buildStepDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build steps. Try reloading the page.')
        }
      })
    },
    getService(): Promise<void> {
      return this.serviceService.getService(this.service ? this.service.id : this.id).then(service => {
        this.service = service
        this.unchangedService = JSON.parse(JSON.stringify(service))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the service. Try reloading the page.')
        }
      })
    },
    onAddFieldClicked(): void {
      this.serviceFieldToEdit = new ServiceField(0)
      this.showServiceFieldModal = true
    },
    onDeleteClicked(id: number): void {
      this.serviceFieldService.deleteServiceField(id).then(() => {
        this.getService().then(() => {
          this.toast.success('Field deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the field. Please try again.')
        }
      })
    },
    onSaveServiceClicked(): void {
      if (!this.service || !this.service.buildStepId) return

      this.saving = true

      if (this.service.id === 0) {
        const createServiceMessage = new CreateServiceMessage(this.service.allowDiscounts, this.service.buildStepId, this.service.description, this.service.name, this.service.price, this.service.sku)
        this.serviceService.createService(createServiceMessage).then(objectCreatedResponse => {
          if (!this.service) return

          this.service.id = objectCreatedResponse.id
          this.getService().then(() => {
            this.$router.replace(`/service/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The service was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the service. Please try again.')
          }
        })
      } else {
        const updateServiceMessage = new UpdateServiceMessage(this.service.allowDiscounts, this.service.description, this.service.name, this.service.price, this.service.sku)
        this.serviceService.updateService(this.id, updateServiceMessage).then(() => {
          this.toast.success('The service was updated successfully.')
          this.$router.push('/services')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the service. Please try again.')
          }
        })
      }
    },
    onTableRowClicked(serviceField: ServiceField): void {
      this.serviceFieldToEdit = serviceField
      this.showServiceFieldModal = true
    },
    typeToDisplayValue(type: number): string {
      switch(type) {
        case 1: return 'Cartridge'
        case 2: return 'Selection'
        default: return ''
      }
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.service)
        document.title = `${this.service.name} - Short Action Customs, LLC`
    }
  }
})
